import { isRecord } from "@arti/utilities";

import type { DashboardRealtimeMessage } from "../../realtime/dashboardRealtimeTypes";
import type {
  BlackboardChannel,
  BlackboardInstance,
  InFlightStepKind,
  InFlightWakeState,
  InstanceStatus,
} from "./blackboardModels";

const VALID_STATUSES: ReadonlySet<string> = new Set<InstanceStatus>([
  "running",
  "waiting",
  "killed",
]);

export function isInstanceStatus(value: string): value is InstanceStatus {
  return VALID_STATUSES.has(value);
}

// ---------------------------------------------------------------------------
// Terminal-state monotonicity.
//
// `killed` is an ABSORBING UI state. A killed actor never wakes, so no
// realtime event can legitimately move it back to running/waiting — but late
// telemetry (a lock event, a token delta's status side-effect, a status_change
// emitted before the kill committed) used to arrive after the kill and
// re-render the actor as ACTING. Timestamp ordering does not save us: the
// activity genuinely happened before the kill, it just arrived after.
//
// The rule lives HERE, in one place, and every realtime reducer routes its
// status writes through it — both the pure reducers below and the inline
// reducers in useBlackboard. Only an event that is itself killed/deleted may
// change a terminal instance.
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES: ReadonlySet<string> = new Set(["killed", "deleted"]);

/** True when this instance has reached an absorbing lifecycle state. */
export function isTerminalInstanceStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Whether a realtime event may mutate this instance at all. Terminal
 * instances accept only terminal events (a redundant kill, or a delete that
 * removes the row).
 */
export function canRealtimeEventMutateInstance(
  current: Pick<BlackboardInstance, "status">,
  eventStatus: string | null,
): boolean {
  if (!isTerminalInstanceStatus(current.status)) {
    return true;
  }
  return eventStatus !== null && isTerminalInstanceStatus(eventStatus);
}

/**
 * Apply a realtime update to one instance, preserving terminal state. Pass
 * `eventStatus` when the event itself carries a lifecycle status; pass null
 * for pure activity/telemetry events (which can never resurrect a kill).
 */
export function applyRealtimeInstanceUpdate(
  instance: BlackboardInstance,
  eventStatus: string | null,
  update: (instance: BlackboardInstance) => BlackboardInstance,
): BlackboardInstance {
  return canRealtimeEventMutateInstance(instance, eventStatus)
    ? update(instance)
    : instance;
}

/**
 * Apply a locally-confirmed kill to the fleet rows.
 *
 * The DELETE has returned, so the kill is committed server-side: this is not an
 * optimistic guess. It must clear the same live activation fields the server's
 * `instance_status_changed` → killed event clears, because advancing the
 * instance's ordering marker on a local kill means that event can arrive with
 * an older marker and be dropped at the marker gate. Without this the actor
 * keeps rendering as mid-activation (and inflating the running/generating
 * counters) until the generating TTL expires.
 */
export function applyLocalKillToInstances(
  instances: readonly BlackboardInstance[],
  instanceId: string,
): BlackboardInstance[] {
  return instances.map((instance) =>
    instance.id === instanceId
      ? {
          ...instance,
          channelLockWaiting: false,
          inFlightStepKind: null,
          inFlightWakeState: null,
          status: "killed" as const,
        }
      : instance,
  );
}

export function shouldApplyInstanceRealtimeMarker(
  lastMarker: number | undefined,
  incomingMarker: number,
): boolean {
  return lastMarker === undefined || incomingMarker >= lastMarker;
}

export function acceptInstanceActivityRealtimeMarker(
  markers: Map<string, number>,
  instanceId: string,
  activityType: string | null,
  marker: number,
): boolean {
  if (activityType === "channel_message") {
    return true;
  }
  if (!shouldApplyInstanceRealtimeMarker(markers.get(instanceId), marker)) {
    return false;
  }
  if (
    activityType === "status_change" ||
    activityType === "channel_lock_waiting" ||
    activityType === "channel_lock_acquired" ||
    activityType === "actor_wake" ||
    activityType === "consolidation_only_wake" ||
    activityType === "external_compact_seed" ||
    activityType === "actor_park"
  ) {
    markers.set(instanceId, marker);
  }
  return true;
}

export function readMessageMarker(
  message: Pick<DashboardRealtimeMessage, "timestamp">,
) {
  return Number.isFinite(message.timestamp) ? message.timestamp : Date.now();
}

export function readNullableString(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === "string" ? value : undefined;
}

/**
 * Whether an `actor_wake` carrying `run_id` means a NEW durable run exists,
 * so the instance aggregate (`runCount`) needs a REST refetch to converge.
 *
 * `runCount` is a database aggregate over durable run rows — it is never
 * incremented client-side: wake events are replayable, a reconnect can drop
 * them, and prior runs may have been missed entirely, so arithmetic drifts.
 * The wake only signals "refetch"; the count itself stays canonical.
 *
 * Requires a known baseline: `previousRunId === undefined` means we hold no
 * run state for that instance (not in the fetched list), and refetching on
 * those would churn on every unrelated wake. A healthy parked actor re-waking
 * into the SAME run reports an unchanged id and refetches nothing.
 */
export function shouldRefetchRunAggregate(
  previousRunId: string | null | undefined,
  wakeRunId: string | null | undefined,
): boolean {
  if (previousRunId === undefined) {
    return false;
  }
  if (typeof wakeRunId !== "string" || wakeRunId.length === 0) {
    return false;
  }
  return wakeRunId !== previousRunId;
}

export function readInstanceActivityStepKind(
  activityType: string | null,
): InFlightStepKind | null {
  return activityType === "actor_wake" ||
    activityType === "consolidation_only_wake" ||
    activityType === "external_compact_seed"
    ? activityType
    : null;
}

function parseInFlightWakeState(value: unknown): InFlightWakeState | null {
  switch (value) {
    case "preparing":
    case "lock_waiting":
    case "llm_call":
    case "between_steps": {
      return value;
    }
    default: {
      return null;
    }
  }
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

export function readInstanceActivityWakeState(input: {
  detail?: unknown;
  wake_state?: unknown;
}): InFlightWakeState | null {
  // Wake_state is a top-level payload field (never inside detail). The
  // Detail.wake_state / detail.state fallbacks were defensive reads for a
  // shape that no producer emits — removed once the schema was typed.
  return parseInFlightWakeState(input.wake_state);
}

export function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function readIsoTimestamp(
  detail: Record<string, unknown> | null,
  key: string,
  fallbackMs: number,
): string {
  const value = detail?.[key];
  return typeof value === "string" && Number.isFinite(Date.parse(value))
    ? value
    : new Date(fallbackMs).toISOString();
}

export function shouldRefreshBlackboardForRealtimeMessage(
  message: Pick<DashboardRealtimeMessage, "type">,
): boolean {
  return (
    message.type === "instance_spawned" ||
    message.type === "actor_instance_changed" ||
    message.type === "channel_changed" ||
    message.type === "template_changed" ||
    message.type === "channel_environment_changed"
  );
}

/**
 * Merge a freshly-fetched instance's contextWindow with the prior one. REST is
 * authoritative for size/growth but has no source for the live phase fields
 * (phaseBudgetRemaining, currentPhase) — it stamps defaults — so a blind refetch
 * would flicker them. Carry the prior WS-derived phase fields forward.
 */
export function mergeContextWindowLiveFields(
  next: BlackboardInstance["contextWindow"],
  prior: BlackboardInstance["contextWindow"],
): BlackboardInstance["contextWindow"] {
  if (!prior) {
    return next;
  }
  if (next) {
    return {
      contextGrowthSinceEvolution: next.contextGrowthSinceEvolution,
      contextWindowSize: next.contextWindowSize,
      currentPhase: prior.currentPhase,
      phaseBudgetRemaining: prior.phaseBudgetRemaining,
    };
  }
  // REST rebuilt null (size 0 && growth 0) but the actor has demonstrably run
  // (prior was live). Preserve prior wholesale; the next WS event re-syncs.
  return prior;
}

/**
 * Carry forward WS-only fields from the prior instance row across a full REST
 * refetch so a reconnect-triggered refresh does not flicker live HUD state
 * (phase/budget/cache/lock-wait) back to defaults. New instances (no prior)
 * keep their REST values.
 *
 * REST is treated as just another update carrying its own lifecycle status, so
 * it routes through the terminal-monotonicity chokepoint above: a snapshot that
 * was taken before a kill committed cannot spread a stale `running` over a
 * locally-killed row. Marker ordering alone does not cover this — the request
 * is in flight when the kill lands, and a same-millisecond marker compares
 * equal — so the guard is on the merge itself.
 */
export function mergeInstancePreservingLiveFields(
  next: BlackboardInstance,
  prior: BlackboardInstance | undefined,
): BlackboardInstance {
  if (!prior) {
    return next;
  }
  return applyRealtimeInstanceUpdate(prior, next.status, () => ({
    ...next,
    contextWindow: mergeContextWindowLiveFields(
      next.contextWindow,
      prior.contextWindow,
    ),
    lastCacheStats: next.lastCacheStats ?? prior.lastCacheStats,
    lastChannelLockWaitMs:
      next.lastChannelLockWaitMs ?? prior.lastChannelLockWaitMs,
  }));
}

export function mergeFetchedInstancesWithPrior(
  nextInstances: readonly BlackboardInstance[],
  priorInstances: readonly BlackboardInstance[],
  markers: Map<string, number>,
  snapshotMarker: number,
): BlackboardInstance[] {
  if (priorInstances.length === 0) {
    for (const inst of nextInstances) {
      markers.set(inst.id, snapshotMarker);
    }
    return [...nextInstances];
  }

  const priorById = new Map(priorInstances.map((inst) => [inst.id, inst]));
  const returnedIds = new Set(nextInstances.map((inst) => inst.id));
  const pendingOptimisticInstances = priorInstances.filter(
    (inst) => inst.isOptimisticSpawn === true && !returnedIds.has(inst.id),
  );
  const mergedInstances = nextInstances.flatMap((inst) => {
    const lastMarker = markers.get(inst.id);
    if (!shouldApplyInstanceRealtimeMarker(lastMarker, snapshotMarker)) {
      const prior = priorById.get(inst.id);
      return prior ? [prior] : [];
    }
    markers.set(inst.id, snapshotMarker);
    return [mergeInstancePreservingLiveFields(inst, priorById.get(inst.id))];
  });
  return [...pendingOptimisticInstances, ...mergedInstances];
}

export interface OptimisticSpawnInput {
  avatarSeed: string;
  displayName: string;
  identityPrompt: string | null;
  modelRef?: string;
  sharedChannelId?: string;
  templateId: string;
  templateModelRef: string | null;
}

export interface OptimisticSpawnResponse {
  channelIds: string[];
  instanceId: string;
}

export function buildOptimisticSpawnInstance(
  input: OptimisticSpawnInput,
  response: OptimisticSpawnResponse,
  createdAt: string,
): BlackboardInstance {
  return {
    actorWakeCacheStats: null,
    avatarSeed: input.avatarSeed,
    channelLockWaiting: false,
    channels: response.channelIds.map((channelId) =>
      buildOptimisticSpawnChannel(channelId, input, response.instanceId),
    ),
    contextWindow: null,
    createdAt,
    currentRunId: null,
    displayName: input.displayName,
    evolution: 0,
    id: response.instanceId,
    identityPrompt: input.identityPrompt,
    inFlightStepKind: "actor_wake",
    inFlightWakeState: "preparing",
    isOptimisticSpawn: true,
    lastActorWakeAt: null,
    lastCacheStats: null,
    lastChannelLockWaitMs: null,
    lastParkedAt: null,
    modelRef: input.modelRef ?? input.templateModelRef,
    organizationId: "",
    overrides: null,
    runCount: 0,
    spawnedBy: null,
    status: "running",
    systemPromptEditedAt: null,
    templateId: input.templateId,
    tickIntervalMs: null,
    tokenUsage: {
      cacheWriteTokens: 0,
      cachedInputTokens: 0,
      inputTokens: 0,
      outputTokens: 0,
    },
    totalCostUsd: 0,
    updatedAt: createdAt,
    wakeBlockedAt: null,
    wakeBlockedConsecutiveFailureCount: null,
    wakeBlockedError: null,
    wakeBlockedReason: null,
    wakeBlockedTriggerChannelMessageId: null,
    wakeBlockedUntil: null,
  };
}

export function pruneMaterializedSpawnIds(
  recentlySpawnedIds: ReadonlySet<string>,
  instances: readonly Pick<BlackboardInstance, "id" | "isOptimisticSpawn">[],
): ReadonlySet<string> {
  const materializedIds = new Set(
    instances
      .filter((instance) => instance.isOptimisticSpawn !== true)
      .map((instance) => instance.id),
  );
  const nextIds = new Set(recentlySpawnedIds);
  for (const instanceId of recentlySpawnedIds) {
    if (materializedIds.has(instanceId)) {
      nextIds.delete(instanceId);
    }
  }
  return nextIds.size === recentlySpawnedIds.size
    ? recentlySpawnedIds
    : nextIds;
}

function buildOptimisticSpawnChannel(
  channelId: string,
  input: Pick<OptimisticSpawnInput, "sharedChannelId">,
  instanceId: string,
): BlackboardChannel {
  const isSharedChannel = channelId === input.sharedChannelId;
  return {
    displayName: isSharedChannel ? "shared" : "private",
    id: channelId,
    kind: isSharedChannel ? "shared" : "private",
    ownerActorId: isSharedChannel ? null : instanceId,
    provenance: "manual",
  };
}

interface InstanceStatusChangedDeltaPayload {
  instance_id?: unknown;
  status?: unknown;
  current_run_id?: unknown;
  wake_blocked_at?: unknown;
  wake_blocked_consecutive_failure_count?: unknown;
  wake_blocked_error?: unknown;
  wake_blocked_reason?: unknown;
  wake_blocked_trigger_channel_message_id?: unknown;
  wake_blocked_until?: unknown;
}

export function applyInstanceStatusChangedDelta(
  instances: BlackboardInstance[],
  markers: Map<string, number>,
  payload: InstanceStatusChangedDeltaPayload,
  marker: number,
) {
  const instanceId =
    typeof payload.instance_id === "string" ? payload.instance_id : null;
  const rawStatus = typeof payload.status === "string" ? payload.status : null;
  if (!instanceId || rawStatus === null) {
    return { clearLiveInstanceId: null, instances, needsRefresh: true };
  }
  if (!shouldApplyInstanceRealtimeMarker(markers.get(instanceId), marker)) {
    return { clearLiveInstanceId: null, instances, needsRefresh: false };
  }
  if (rawStatus === "deleted") {
    markers.set(instanceId, marker);
    return {
      clearLiveInstanceId: instanceId,
      instances: instances.filter((inst) => inst.id !== instanceId),
      needsRefresh: false,
    };
  }
  const newStatus = isInstanceStatus(rawStatus) ? rawStatus : null;
  if (!newStatus) {
    return { clearLiveInstanceId: null, instances, needsRefresh: true };
  }
  const currentRunId = readNullableString(payload.current_run_id);
  const wakeBlockedAt = readNullableString(payload.wake_blocked_at);
  const wakeBlockedUntil = readNullableString(payload.wake_blocked_until);
  const wakeBlockedReason = readNullableString(payload.wake_blocked_reason);
  const wakeBlockedError = readNullableString(payload.wake_blocked_error);
  const wakeBlockedTriggerChannelMessageId = readNullableString(
    payload.wake_blocked_trigger_channel_message_id,
  );
  const wakeBlockedConsecutiveFailureCount = readNullableNumber(
    payload.wake_blocked_consecutive_failure_count,
  );
  markers.set(instanceId, marker);
  const shouldClearLiveState =
    newStatus === "waiting" || newStatus === "killed";
  return {
    clearLiveInstanceId: shouldClearLiveState ? instanceId : null,
    instances: instances.map((inst) => {
      if (inst.id !== instanceId) {
        return inst;
      }
      // `newStatus` is the event's own lifecycle status: a killed event may
      // land on a killed instance, a running one may not.
      return applyRealtimeInstanceUpdate(inst, newStatus, (current) => ({
        ...current,
        ...(shouldClearLiveState && { channelLockWaiting: false }),
        ...(shouldClearLiveState && { inFlightStepKind: null }),
        ...(shouldClearLiveState && { inFlightWakeState: null }),
        ...(currentRunId !== undefined && { currentRunId }),
        ...(newStatus === "running" && {
          wakeBlockedAt: null,
          wakeBlockedConsecutiveFailureCount: null,
          wakeBlockedError: null,
          wakeBlockedReason: null,
          wakeBlockedTriggerChannelMessageId: null,
          wakeBlockedUntil: null,
        }),
        ...(wakeBlockedAt !== undefined && { wakeBlockedAt }),
        ...(wakeBlockedUntil !== undefined && { wakeBlockedUntil }),
        ...(wakeBlockedReason !== undefined && { wakeBlockedReason }),
        ...(wakeBlockedError !== undefined && { wakeBlockedError }),
        ...(wakeBlockedTriggerChannelMessageId !== undefined && {
          wakeBlockedTriggerChannelMessageId,
        }),
        ...(wakeBlockedConsecutiveFailureCount !== undefined && {
          wakeBlockedConsecutiveFailureCount,
        }),
        status: newStatus,
      }));
    }),
    needsRefresh: false,
  };
}

interface InstanceActivityLifecyclePayload {
  activity_type?: unknown;
  current_run_id?: unknown;
  detail?: unknown;
  instance_id?: unknown;
  wake_state?: unknown;
}

export function applyInstanceActivityLifecycleDelta(
  instances: BlackboardInstance[],
  payload: InstanceActivityLifecyclePayload,
  timestamp: number,
): { clearLiveInstanceId: string | null; instances: BlackboardInstance[] } {
  const instanceId =
    typeof payload.instance_id === "string" ? payload.instance_id : null;
  const activityType =
    typeof payload.activity_type === "string" ? payload.activity_type : null;
  if (!instanceId || activityType === null) {
    return { clearLiveInstanceId: null, instances };
  }

  const activityCurrentRunId = readNullableString(payload.current_run_id);
  const detail = readRecord(payload.detail);
  const wakeState = readInstanceActivityWakeState({
    detail: payload.detail,
    wake_state: payload.wake_state,
  });
  // Activity events carry no lifecycle status of their own, so they can never
  // move an instance out of `killed`/`deleted` (see
  // applyRealtimeInstanceUpdate).
  const updateInstance = (
    update: (instance: BlackboardInstance) => BlackboardInstance,
  ) => ({
    clearLiveInstanceId: null,
    instances: instances.map((inst) =>
      inst.id === instanceId
        ? applyRealtimeInstanceUpdate(inst, null, update)
        : inst,
    ),
  });

  if (activityType === "channel_lock_waiting") {
    return updateInstance((inst) => ({
      ...inst,
      ...(activityCurrentRunId !== undefined && {
        currentRunId: activityCurrentRunId,
      }),
      channelLockWaiting: true,
      inFlightStepKind: "actor_wake",
      inFlightWakeState: "lock_waiting",
      status: "running",
    }));
  }

  if (activityType === "channel_lock_acquired") {
    const waitedMs =
      detail !== null && typeof detail.waited_ms === "number"
        ? detail.waited_ms
        : null;
    return updateInstance((inst) => ({
      ...inst,
      ...(activityCurrentRunId !== undefined && {
        currentRunId: activityCurrentRunId,
      }),
      channelLockWaiting: false,
      inFlightStepKind: "actor_wake",
      inFlightWakeState: wakeState ?? "between_steps",
      lastChannelLockWaitMs: waitedMs,
      status: "running",
    }));
  }

  const stepKind = readInstanceActivityStepKind(activityType);
  if (stepKind !== null) {
    const wokeAt =
      detail !== null &&
      typeof detail.woke_at === "string" &&
      Number.isFinite(Date.parse(detail.woke_at))
        ? detail.woke_at
        : null;
    return updateInstance((inst) => ({
      ...inst,
      ...(activityCurrentRunId !== undefined && {
        currentRunId: activityCurrentRunId,
      }),
      channelLockWaiting:
        stepKind === "actor_wake" && wakeState === "lock_waiting"
          ? true
          : inst.channelLockWaiting && wakeState === null,
      inFlightStepKind: stepKind,
      inFlightWakeState: wakeState,
      ...(stepKind === "actor_wake" &&
        wokeAt !== null && { lastActorWakeAt: wokeAt }),
      status: "running",
      wakeBlockedAt: null,
      wakeBlockedConsecutiveFailureCount: null,
      wakeBlockedError: null,
      wakeBlockedReason: null,
      wakeBlockedTriggerChannelMessageId: null,
      wakeBlockedUntil: null,
    }));
  }

  if (activityType === "actor_park") {
    const parkedAt = readIsoTimestamp(detail, "parked_at", timestamp);
    return {
      clearLiveInstanceId: instanceId,
      instances: instances.map((inst) =>
        inst.id === instanceId
          ? applyRealtimeInstanceUpdate(inst, null, (current) => ({
              ...current,
              channelLockWaiting: false,
              ...(activityCurrentRunId !== undefined && {
                currentRunId: activityCurrentRunId,
              }),
              inFlightStepKind: null,
              inFlightWakeState: null,
              lastParkedAt: parkedAt,
              status: "waiting",
            }))
          : inst,
      ),
    };
  }

  return { clearLiveInstanceId: null, instances };
}

function readNullableNumber(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

// ---------------------------------------------------------------------------
// Hook: useBlackboard
// Initial REST fetch on mount, then live WS deltas via internalEventTopic.
// Instance events: instance_spawned, instance_status_changed.
// TODO(blackboard): Replace raw fetch calls with generated dashboard-client
// Functions once the instance API spec is generated.
// ---------------------------------------------------------------------------

// How long after the last token_delta before we clear the "generating" state.
